import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Ban, RotateCcw } from 'lucide-react';
import apiClient from '@/services/api';
import type { ExceptionsReport as ExceptionsReportData, ExceptionsSection } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

const HEADERS = ['Ticket / Item', 'Qty', 'Unit Price', 'Total'];

/** Exceptions: voided and refunded tickets, each with its line items listed and a total per section. */
export default function ExceptionsReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<ExceptionsReportData>({
    queryKey: ['exceptions-report', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getExceptionsReport(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const voided = data?.voided ?? { tickets: [], ticketCount: 0, totalAmount: 0 };
  const refunded = data?.refunded ?? { tickets: [], ticketCount: 0, totalAmount: 0 };
  const isEmpty = voided.ticketCount === 0 && refunded.ticketCount === 0;
  const num = (n: number) => Number(n ?? 0).toLocaleString();
  const fmtDateTime = (s: string | null) => (s ? format(parseISO(s), 'dd-MM-yyyy HH:mm') : '—');
  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });

  // Ticket header row followed by its indented line items, same "↳" pattern used
  // by the Menu Insights export for item sub-rows.
  const rowsFor = (section: ExceptionsSection): (string | number)[][] => {
    const rows: (string | number)[][] = [];
    section.tickets.forEach((t) => {
      rows.push([
        `#${t.ticketNumber ?? t.posTicketId ?? '—'} · ${t.orderType ?? '—'} · ${t.serverName ?? '—'} · ${fmtDateTime(t.closedAt)}`,
        '',
        '',
        formatCurrency(t.totalAmount),
      ]);
      t.items.forEach((it) => {
        rows.push([`↳ ${it.name}`, num(it.quantity), formatCurrency(it.unitPrice), formatCurrency(it.total)]);
      });
    });
    return rows;
  };
  const footerFor = (section: ExceptionsSection): (string | number)[] => ['TOTAL', '', '', formatCurrency(section.totalAmount)];

  const sections = (): ReportSection[] => [
    { heading: 'Voided Tickets', headers: HEADERS, rows: [...rowsFor(voided), footerFor(voided)] },
    { heading: 'Refunded Tickets', headers: HEADERS, rows: [...rowsFor(refunded), footerFor(refunded)] },
  ];
  const kpis = (): [string, string][] => [
    ['Voided Tickets', num(voided.ticketCount)],
    ['Voided Total', formatCurrency(voided.totalAmount)],
    ['Refunded Tickets', num(refunded.ticketCount)],
    ['Refunded Total', formatCurrency(refunded.totalAmount)],
  ];

  return (
    <ReportFrame
      title="Exceptions"
      description="Voided and refunded tickets with their line items, and a total per section."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={isEmpty}
      emptyHint="No voided or refunded tickets in range"
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`exceptions-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Exceptions', meta(), kpis(), sections()))}
      onExportPdf={() => data && printGenericReport('Exceptions Report', meta(), kpis(), sections())}
    >
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <KPICard label="Voided Tickets" value={num(voided.ticketCount)} icon={Ban} palette="red" />
        <KPICard label="Voided Total" value={formatCurrency(voided.totalAmount)} icon={Ban} palette="red" />
        <KPICard label="Refunded Tickets" value={num(refunded.ticketCount)} icon={RotateCcw} palette="purple" />
        <KPICard label="Refunded Total" value={formatCurrency(refunded.totalAmount)} icon={RotateCcw} palette="purple" />
      </div>

      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Voided Tickets ({voided.ticketCount})</h3>
        {voided.tickets.length === 0 ? (
          <p className="text-sm text-gray-500">No voided tickets in range.</p>
        ) : (
          <SimpleTable headers={HEADERS} rows={rowsFor(voided)} footer={footerFor(voided)} />
        )}
      </div></div>

      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Refunded Tickets ({refunded.ticketCount})</h3>
        {refunded.tickets.length === 0 ? (
          <p className="text-sm text-gray-500">No refunded tickets in range.</p>
        ) : (
          <SimpleTable headers={HEADERS} rows={rowsFor(refunded)} footer={footerFor(refunded)} />
        )}
      </div></div>
    </ReportFrame>
  );
}
